import Switch, {type SwitchProps} from '@mui/material/Switch';
import {useRef, useState} from 'react';
import clsx from 'clsx';
import './ArrToggle.scss';

export type ArrToggleProps = SwitchProps & {
  ariaLabel?: string;
  className?: string;
  dataTestId: string;
  disabled?: boolean;
  toggleSize?: 'large' | 'medium' | 'small' | 'xs';
  onCustomKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
};

export const ArrToggle = (props: ArrToggleProps) => {
  const {
    disabled = false,
    ariaLabel,
    className,
    dataTestId,
    toggleSize = 'large',
    onChange,
    onCustomKeyDown,
    ...rest
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const switchRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && onChange && !disabled) {
      event.preventDefault();
      event.stopPropagation();
      // Get the actual input element to read its checked state
      const inputElement = switchRef.current?.querySelector(
        'input[type="checkbox"]'
      ) as HTMLInputElement | null;
      const currentChecked = inputElement
        ? inputElement.checked
        : !!props.value;
      const newChecked = !currentChecked;
      const syntheticEvent = {
        target: {value: newChecked, checked: newChecked},
        currentTarget: {value: newChecked, checked: newChecked},
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent, newChecked);
    }
  };

  return (
    <div
      className={clsx('arr-switch-wrapper', `arr-switch--${toggleSize}`, {
        'is-focused': isFocused,
      })}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
        if (onCustomKeyDown) {
          onCustomKeyDown(e);
        }
        handleKeyDown(e);
      }}
      ref={switchRef}
      data-testid={`arr-toggle-div-${dataTestId}`}
    >
      <Switch
        className={className ? `${className} arr-switch` : 'arr-switch'}
        disabled={disabled}
        disableRipple
        data-testid={dataTestId}
        checked={!!props.value}
        onChange={onChange}
        aria-label={ariaLabel}
        slotProps={{input: {'data-testid': `${dataTestId}-toggle`}}}
        {...rest}
      />
    </div>
  );
};
